// ---------------------------------------------------------------------------
// Model switcher hooks — ae10-only feature
// ---------------------------------------------------------------------------
// Talks to the bridge proxy routes added in Phase 2:
//   GET    /api/models/switchable?agentId=…&sessionId=…
//   GET    /api/models/current?agentId=…&sessionId=…
//   PUT    /api/models/current?agentId=…&sessionId=…   body { model }
//   DELETE /api/models/current?agentId=…&sessionId=…
//
// The backend reject non-ae10 callers with 403 inside createModelsRouter().
// We still surface the 403 here so the dropdown can show a graceful
// "not available" state instead of leaving the user with an unexplained
// failure.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useState } from "react";
import { useRefreshOnReconnect } from "@/hooks/use-backend-connectivity";
import { fetchWithAuth, isAuthNotReadyError } from "@/lib/utils";
import i18n from "@/lib/i18n";

// ---------------------------------------------------------------------------
// Types — mirror the agno-server response shapes
// ---------------------------------------------------------------------------

export interface SwitchableModelsResponse {
  /** The model string the agent uses by default. */
  current: string;
  /** Compatibility group for `available`, or `all` for an empty session. */
  group: string;
  /** Switchable aliases. An empty session receives every registered alias. */
  available: string[];
  /** Optional on newer servers; older responses continue to parse normally. */
  unavailable?: Array<{
    alias: string;
    provider: string;
    requires_key: boolean;
  }>;
}

export interface SessionModelResponse {
  session_id: string;
  agent_id: string;
  /** The active model slug (override or default). */
  model: string;
  /** True if the active model is a session-level override (not the agent
   *  default). When false, the dropdown hides the "Reset" action. */
  is_override: boolean;
  /** The agent's default model slug. */
  default: string;
}

export interface UseSwitchableModelsReturn {
  data: SwitchableModelsResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export interface UseSessionModelReturn {
  data: SessionModelResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export interface UseSwitchSessionModelReturn {
  switching: boolean;
  /**
   * Switch the model for `agentId` + `sessionId` to `slug`.
   * Returns true on success. Errors are surfaced via the returned
   * object so the caller can show a toast or inline message.
   */
  switchModel: (
    agentId: string,
    sessionId: string,
    slug: string,
  ) => Promise<{ ok: true } | { ok: false; status: number; error: string }>;
  /**
   * Revert the session to its agent's default model.
   */
  resetModel: (
    agentId: string,
    sessionId: string,
  ) => Promise<{ ok: true } | { ok: false; status: number; error: string }>;
}

// ---------------------------------------------------------------------------
// useSwitchableModels — read models compatible with an agent/session
// ---------------------------------------------------------------------------

export function useSwitchableModels(
  agentId: string | null,
  sessionId: string | null,
  getToken: () => Promise<string>,
): UseSwitchableModelsReturn {
  const [data, setData] = useState<SwitchableModelsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!agentId) {
      setData(null);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const url =
        `/api/models/switchable?agentId=${encodeURIComponent(agentId)}` +
        (sessionId ? `&sessionId=${encodeURIComponent(sessionId)}` : "");
      const res = await fetchWithAuth(
        url,
        { headers: {} },
        getToken,
      );
      if (!res.ok) {
        setData(null);
        setError(
          res.status === 403
            ? i18n.t("modelSwitcher.unavailableTenant")
            : i18n.t("modelSwitcher.loadFailed", { status: res.status }),
        );
        return;
      }
      const body = (await res.json()) as SwitchableModelsResponse;
      setData(body);
    } catch (err) {
      if (isAuthNotReadyError(err)) {
        // Auth still loading — let the watchdog in useAuth catch up.
        setData(null);
        return;
      }
      setData(null);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [agentId, sessionId, getToken]);

  useRefreshOnReconnect(refresh, Boolean(agentId));

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { data, loading, error, refresh };
}

// ---------------------------------------------------------------------------
// useSessionModel — read the active model for an (agent, session) pair
// ---------------------------------------------------------------------------

export function useSessionModel(
  agentId: string | null,
  sessionId: string | null,
  getToken: () => Promise<string>,
): UseSessionModelReturn {
  const [data, setData] = useState<SessionModelResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!agentId || !sessionId) {
      setData(null);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const url =
        `/api/models/current?agentId=${encodeURIComponent(agentId)}` +
        `&sessionId=${encodeURIComponent(sessionId)}`;
      const res = await fetchWithAuth(url, { headers: {} }, getToken);
      if (!res.ok) {
        setData(null);
        setError(
          res.status === 403
            ? i18n.t("modelSwitcher.unavailableTenant")
            : i18n.t("modelSwitcher.activeLoadFailed", { status: res.status }),
        );
        return;
      }
      const body = (await res.json()) as SessionModelResponse;
      setData(body);
    } catch (err) {
      if (isAuthNotReadyError(err)) {
        setData(null);
        return;
      }
      setData(null);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [agentId, sessionId, getToken]);

  useRefreshOnReconnect(refresh, Boolean(agentId && sessionId));

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { data, loading, error, refresh };
}

// ---------------------------------------------------------------------------
// useSwitchSessionModel — PUT (switch) and DELETE (reset) actions
// ---------------------------------------------------------------------------
// After a successful mutation the caller is responsible for refetching both
// `useSwitchableModels` and `useSessionModel` (the dropdown does this in
// one place). We deliberately do NOT thread refresh functions through the
// action hook — that would force every consumer to pass two extra refresh
// functions and tie them to component identity. The simpler contract is:
//   "tell me what happened; the caller knows which read hooks to refresh".
// ---------------------------------------------------------------------------

export function useSwitchSessionModel(
  getToken: () => Promise<string>,
): UseSwitchSessionModelReturn {
  const [switching, setSwitching] = useState(false);

  const switchModel = useCallback(
    async (
      agentId: string,
      sessionId: string,
      slug: string,
    ): Promise<{ ok: true } | { ok: false; status: number; error: string }> => {
      setSwitching(true);
      try {
        const url =
          `/api/models/current?agentId=${encodeURIComponent(agentId)}` +
          `&sessionId=${encodeURIComponent(sessionId)}`;
        const res = await fetchWithAuth(
          url,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ model: slug }),
          },
          getToken,
        );
        if (res.ok) return { ok: true };
        const errText = await res.text().catch(() => `HTTP ${res.status}`);
        return {
          ok: false as const,
          status: res.status,
          error: errText || `HTTP ${res.status}`,
        };
      } catch (err) {
        if (isAuthNotReadyError(err)) {
          return { ok: false as const, status: 0, error: i18n.t("auth.sessionNotReady") };
        }
        return {
          ok: false as const,
          status: 0,
          error: err instanceof Error ? err.message : String(err),
        };
      } finally {
        setSwitching(false);
      }
    },
    [getToken],
  );

  const resetModel = useCallback(
    async (
      agentId: string,
      sessionId: string,
    ): Promise<{ ok: true } | { ok: false; status: number; error: string }> => {
      setSwitching(true);
      try {
        const url =
          `/api/models/current?agentId=${encodeURIComponent(agentId)}` +
          `&sessionId=${encodeURIComponent(sessionId)}`;
        const res = await fetchWithAuth(
          url,
          { method: "DELETE" },
          getToken,
        );
        if (res.ok) return { ok: true };
        const errText = await res.text().catch(() => `HTTP ${res.status}`);
        return {
          ok: false as const,
          status: res.status,
          error: errText || `HTTP ${res.status}`,
        };
      } catch (err) {
        if (isAuthNotReadyError(err)) {
          return { ok: false as const, status: 0, error: i18n.t("auth.sessionNotReady") };
        }
        return {
          ok: false as const,
          status: 0,
          error: err instanceof Error ? err.message : String(err),
        };
      } finally {
        setSwitching(false);
      }
    },
    [getToken],
  );

  return { switching, switchModel, resetModel };
}
